import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import { useAppStore } from '../stores/app-store';
import { api } from '../lib/api';
import { useKeyboardShortcuts } from '../hooks/use-keyboard-shortcuts';
import {
  Search,
  Send,
  MoreVertical,
  ArrowLeft,
  MessageSquare,
  UserCircle,
  Paperclip,
  BookOpen,
  FileText,
  Download,
  X,
  Smile,
  ExternalLink,
  Star,
  Mic,
  Loader2,
} from 'lucide-react';
import EmojiPicker, { type EmojiClickData, Theme as EmojiTheme } from 'emoji-picker-react';
import { cn } from '../lib/utils';
import { motion } from 'motion/react';
import { DefaultAvatar } from './default-avatar';
import { useTheme } from '../hooks/use-theme';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from './ui/responsive-modal';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import { EmptyState } from './ui/empty-state';
import {
  useVoiceRecorder,
  RecordingBar,
  AudioMessage,
  blobToDataUrl,
} from './voice-message';

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// ----- Language flag mapping -----

const LANGUAGE_FLAG_MAP: Record<string, string> = {
  // Portuguese
  português: 'br',
  portuguese: 'br',
  'pt-br': 'br',
  'pt-pt': 'pt',
  pt: 'br',
  // English
  inglês: 'us',
  english: 'us',
  en: 'us',
  'en-us': 'us',
  'en-gb': 'gb',
  // Spanish
  espanhol: 'es',
  spanish: 'es',
  español: 'es',
  es: 'es',
  // French
  francês: 'fr',
  french: 'fr',
  français: 'fr',
  fr: 'fr',
  // German
  alemão: 'de',
  german: 'de',
  deutsch: 'de',
  de: 'de',
  // Italian
  italiano: 'it',
  italian: 'it',
  it: 'it',
  // Japanese
  japonês: 'jp',
  japanese: 'jp',
  ja: 'jp',
  // Chinese
  chinês: 'cn',
  chinese: 'cn',
  zh: 'cn',
  // Russian
  russo: 'ru',
  russian: 'ru',
  ru: 'ru',
  // Arabic
  árabe: 'sa',
  arabic: 'sa',
  ar: 'sa',
  // Dutch
  holandês: 'nl',
  dutch: 'nl',
  nl: 'nl',
  // Korean
  coreano: 'kr',
  korean: 'kr',
  ko: 'kr',
  // Polish
  polonês: 'pl',
  polish: 'pl',
  pl: 'pl',
};

function getLanguageFlag(lang: string): string | null {
  return LANGUAGE_FLAG_MAP[lang.toLowerCase().trim()] ?? null;
}

// ----- Types -----

interface ParticipantUser {
  id: string;
  name: string;
  avatarUrl?: string;
}

interface Participant {
  id?: string;
  userId: string;
  user: ParticipantUser;
}

interface MessageMetadata {
  type: 'book' | 'file' | 'audio';
  // book
  projectId?: string;
  title?: string;
  coverImage?: string;
  // file
  name?: string;
  mime?: string;
  dataUrl?: string;
  size?: number;
  // audio (voice message)
  duration?: number; // seconds
  peaks?: number[]; // normalised waveform, ~40 samples
}

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  metadata?: MessageMetadata | null;
  createdAt: string;
  sender: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
}

interface Conversation {
  id: string;
  updatedAt: string;
  participants: Participant[];
  lastMessage?: Message;
  unreadCount: number;
  otherParticipants?: Participant[];
}

interface ProfessionalInfo {
  id: string;
  type: string;
  title: string;
  bio?: string;
  rating?: number;
  reviewCount?: number;
  hourlyRate?: number;
  specialties?: string[];
  languages?: string[];
  isAvailable?: boolean;
  user: { id: string; name: string; avatarUrl?: string };
}

// ----- Helpers -----

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function resolveOtherUser(
  conversation: Conversation,
  currentUserId: string | undefined
): ParticipantUser | null {
  if (
    conversation.otherParticipants &&
    conversation.otherParticipants.length > 0
  ) {
    return conversation.otherParticipants[0].user ?? null;
  }
  if (conversation.participants && conversation.participants.length > 0) {
    const other = conversation.participants.find(
      (p) => p.userId !== currentUserId
    );
    return other?.user ?? conversation.participants[0].user ?? null;
  }
  return null;
}

function formatMessageTime(dateStr: string, t: ReturnType<typeof useTranslation>['t']): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays === 0)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return t('messagesView.yesterday', 'Ontem');
  if (diffDays < 7)
    return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function showToast(message: string, isError = false) {
  const el = document.createElement('div');
  el.textContent = message;
  el.style.cssText = [
    'position:fixed',
    'bottom:24px',
    'left:50%',
    'transform:translateX(-50%)',
    `background:${isError ? 'var(--destructive)' : 'var(--primary)'}`,
    `color:${isError ? 'var(--destructive-foreground)' : 'var(--primary-foreground)'}`,
    'padding:8px 20px',
    'border-radius:999px',
    'font-size:13px',
    'font-weight:500',
    'z-index:9999',
    'pointer-events:none',
    'opacity:1',
    'transition:opacity 0.4s ease',
  ].join(';');
  document.body.appendChild(el);
  setTimeout(() => {
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 400);
  }, 2500);
}

function isImageMime(mime: string) {
  return mime.startsWith('image/');
}

function isPdfMime(mime: string) {
  return mime === 'application/pdf';
}

// ----- Sub-components -----

function UserAvatar({
  user,
  size = 'md',
}: {
  user: ParticipantUser;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const sizeClasses = {
    // rounded-lg = softer than circle but not fully round
    sm: 'w-8 h-8 text-xs rounded-lg',
    md: 'w-10 h-10 text-sm rounded-lg',
    lg: 'w-11 h-11 text-sm rounded-lg',
    xl: 'w-20 h-20 text-xl rounded-xl',
  };
  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt={user.name}
        className={cn(sizeClasses[size], 'object-cover flex-shrink-0')}
      />
    );
  }
  // sem foto: avatar default deterministico (boring-avatars), sutil na paleta
  return (
    <span
      aria-label={user.name}
      className={cn(sizeClasses[size], 'block overflow-hidden flex-shrink-0 select-none')}
    >
      <DefaultAvatar name={user.id || user.name} />
    </span>
  );
}

// Skeleton row for conversation list loading state
function ConversationSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 animate-pulse">
      <div className="h-11 w-11 rounded-lg bg-muted shrink-0" />
      <div className="flex-1 space-y-2 min-w-0">
        <div className="flex justify-between gap-2">
          <div className="h-3.5 w-28 bg-muted rounded" />
          <div className="h-3 w-10 bg-muted rounded" />
        </div>
        <div className="h-3 w-40 bg-muted rounded" />
      </div>
    </div>
  );
}

function ConversationItem({
  conversation,
  isActive,
  currentUserId,
  onClick,
}: {
  conversation: Conversation;
  isActive: boolean;
  currentUserId: string | undefined;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const otherUser = resolveOtherUser(conversation, currentUserId);
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 text-left border-l-2 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isActive
          ? 'bg-primary/10 border-primary'
          : 'border-transparent hover:bg-muted/60'
      )}
    >
      {otherUser ? (
        <UserAvatar user={otherUser} size="lg" />
      ) : (
        <div className="w-11 h-11 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
          <UserCircle size={18} className="text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-foreground truncate text-sm">
            {otherUser?.name ?? t('messagesView.conversationFallback', 'Conversa')}
          </span>
          {conversation.lastMessage && (
            <span className="text-[10px] font-mono text-muted-foreground flex-shrink-0">
              {formatMessageTime(conversation.lastMessage.createdAt, t)}
            </span>
          )}
        </div>
        {conversation.lastMessage && (
          <p className="text-xs text-muted-foreground truncate mt-0.5 leading-relaxed">
            {conversation.lastMessage.content}
          </p>
        )}
      </div>
      {conversation.unreadCount > 0 && (
        <span className="h-5 min-w-5 flex items-center justify-center bg-accent text-accent-foreground text-[10px] font-semibold rounded-full px-1 flex-shrink-0">
          {conversation.unreadCount}
        </span>
      )}
    </button>
  );
}

// Book card for metadata.type === 'book'
function BookCard({
  metadata,
  isOwn,
}: {
  metadata: MessageMetadata;
  isOwn: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <button
      onClick={() => {
        if (metadata.projectId) navigate(`/app/editor/${metadata.projectId}`);
      }}
      className={cn(
        'flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left w-full max-w-[260px] group',
        isOwn
          ? 'bg-primary/15 border-primary/40 hover:bg-primary/25 hover:border-primary/60'
          : 'bg-muted/60 border-border hover:bg-muted hover:border-primary/40'
      )}
      title={t('messagesView.openBookInEditor', 'Clique para abrir o livro no editor')}
    >
      {metadata.coverImage ? (
        <img
          src={metadata.coverImage}
          alt={metadata.title}
          className="w-12 h-16 object-cover rounded-md flex-shrink-0 shadow-md"
        />
      ) : (
        <div className="w-12 h-16 bg-primary/15 rounded-md flex items-center justify-center flex-shrink-0 border border-primary/20">
          <BookOpen size={18} className="text-primary" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px] font-mono uppercase tracking-widest text-primary font-semibold">
            {t('messagesView.sharedBook', 'Livro compartilhado')}
          </span>
        </div>
        <p className="text-sm font-semibold leading-snug text-foreground line-clamp-2">
          {metadata.title ?? t('messagesView.untitled', 'Sem título')}
        </p>
        <p className="text-[11px] text-muted-foreground mt-1 font-mono flex items-center gap-1 group-hover:text-primary transition-colors">
          <ExternalLink size={10} />
          {t('messagesView.openInEditor', 'Abrir no editor')}
        </p>
      </div>
    </button>
  );
}

// Image / GIF inline with lightbox
function ImageMessage({
  metadata,
}: {
  metadata: MessageMetadata;
}) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(false);
  const isGif = metadata.mime === 'image/gif';

  return (
    <>
      <button
        onClick={() => setLightbox(true)}
        className="block rounded-xl overflow-hidden max-w-[260px] hover:opacity-90 transition-opacity shadow-md"
        title={isGif ? t('messagesView.viewGif', 'Ver GIF') : t('messagesView.viewImage', 'Ver imagem')}
      >
        <img
          src={metadata.dataUrl}
          alt={metadata.name ?? t('messagesView.image', 'imagem')}
          className="max-w-[260px] max-h-[200px] object-cover rounded-xl"
        />
      </button>
      {lightbox && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setLightbox(false)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white bg-black/40 rounded-full p-1.5 transition-colors"
            onClick={() => setLightbox(false)}
            aria-label={t('messagesView.close', 'Fechar')}
          >
            <X size={22} />
          </button>
          <img
            src={metadata.dataUrl}
            alt={metadata.name ?? t('messagesView.image', 'imagem')}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          {metadata.name && (
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/60 font-mono bg-black/40 px-3 py-1 rounded-full">
              {metadata.name}
            </p>
          )}
        </div>
      )}
    </>
  );
}

// PDF card with inline preview
function PdfCard({
  metadata,
  isOwn,
}: {
  metadata: MessageMetadata;
  isOwn: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const openInTab = () => {
    if (!metadata.dataUrl) return;
    const win = window.open();
    if (win) {
      win.document.write(
        `<iframe src="${metadata.dataUrl}" style="width:100%;height:100%;border:none;"></iframe>`
      );
    }
  };

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden w-full max-w-[280px]',
        isOwn ? 'bg-primary/15 border-primary/40' : 'bg-muted/60 border-border'
      )}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0">
          <FileText size={15} className="text-red-500" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate text-foreground">
            {metadata.name ?? t('messagesView.defaultPdfName', 'documento.pdf')}
          </p>
          <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wide">
            PDF{metadata.size ? ` · ${formatFileSize(metadata.size)}` : ''}
          </p>
        </div>
      </div>

      {/* Preview toggle */}
      {metadata.dataUrl && (
        <>
          {expanded && (
            <div
              className="border-t border-border cursor-pointer"
              onClick={() => setExpanded(false)}
            >
              <iframe
                src={metadata.dataUrl}
                className="w-full h-[180px] pointer-events-none"
                title={metadata.name ?? 'PDF'}
              />
              <p className="text-center text-[10px] text-muted-foreground py-1 font-mono">
                {t('messagesView.clickToCollapse', 'Clique para recolher')}
              </p>
            </div>
          )}
          {/* Action buttons */}
          <div className="flex border-t border-border">
            {!expanded && (
              <button
                onClick={() => setExpanded(true)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <FileText size={12} />
                {t('messagesView.preview', 'Preview')}
              </button>
            )}
            <button
              onClick={openInTab}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
            >
              <ExternalLink size={12} />
              {t('messagesView.open', 'Abrir')}
            </button>
            <a
              href={metadata.dataUrl}
              download={metadata.name ?? t('messagesView.defaultPdfName', 'documento.pdf')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Download size={12} />
              {t('messagesView.download', 'Baixar')}
            </a>
          </div>
        </>
      )}
    </div>
  );
}

// Generic file card (DOCX, TXT, etc.)
function FileCard({
  metadata,
  isOwn,
}: {
  metadata: MessageMetadata;
  isOwn: boolean;
}) {
  const { t } = useTranslation();
  const ext = metadata.name?.split('.').pop()?.toUpperCase() ?? t('messagesView.fileExtFallback', 'ARQUIVO');

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-xl border w-full max-w-[260px]',
        isOwn ? 'bg-primary/15 border-primary/40' : 'bg-muted/60 border-border'
      )}
    >
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <FileText size={15} className="text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate text-foreground">
          {metadata.name ?? t('messagesView.fileNameFallback', 'arquivo')}
        </p>
        <p className="text-[10px] font-mono text-muted-foreground">
          {ext}{metadata.size ? ` · ${formatFileSize(metadata.size)}` : ''}
        </p>
      </div>
      {metadata.dataUrl && (
        <a
          href={metadata.dataUrl}
          download={metadata.name}
          className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md hover:bg-muted"
          title={t('messagesView.downloadFile', 'Baixar arquivo')}
          onClick={(e) => e.stopPropagation()}
        >
          <Download size={14} />
        </a>
      )}
    </div>
  );
}

// Unified file chip dispatcher
function FileChip({
  metadata,
  isOwn,
}: {
  metadata: MessageMetadata;
  isOwn: boolean;
}) {
  const mime = metadata.mime ?? '';

  if (isImageMime(mime) && metadata.dataUrl) {
    return <ImageMessage metadata={metadata} />;
  }

  if (isPdfMime(mime) && metadata.dataUrl) {
    return <PdfCard metadata={metadata} isOwn={isOwn} />;
  }

  return <FileCard metadata={metadata} isOwn={isOwn} />;
}

// Message Bubble — renders text, book card, or file chip
function MessageBubble({
  message,
  isOwn,
  showAvatar,
}: {
  message: Message;
  isOwn: boolean;
  showAvatar: boolean;
}) {
  const { t } = useTranslation();
  const meta = message.metadata;
  const hasSpecialContent =
    meta && (meta.type === 'book' || meta.type === 'file' || meta.type === 'audio');

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26 }}
      className={cn(
        'flex items-end gap-2 max-w-[78%]',
        isOwn ? 'ml-auto flex-row-reverse' : 'mr-auto'
      )}
    >
      {/* Avatar slot — always reserves space to keep bubbles aligned */}
      {!isOwn && (
        <div className="flex-shrink-0 w-8">
          {showAvatar ? <UserAvatar user={message.sender} size="sm" /> : null}
        </div>
      )}

      <div className="flex min-w-0 flex-col gap-0.5">
        <div
          className={cn(
            hasSpecialContent
              ? 'rounded-2xl p-0 bg-transparent'
              : 'rounded-[14px] px-4 py-2.5',
            // "Tail" via a 4px corner on the speaker's side — matches the
            // reformu.lat bubble language (bub.me / bub.them).
            !hasSpecialContent && isOwn && 'rounded-br-[4px]',
            !hasSpecialContent && !isOwn &&
              'rounded-bl-[4px] bg-card shadow-[0_0_0_1px_var(--border)_inset] text-foreground'
          )}
          style={
            !hasSpecialContent && isOwn
              ? { background: 'linear-gradient(135deg,var(--ember),var(--ember-hot))', color: '#141005' }
              : undefined
          }
        >
          {hasSpecialContent ? (
            meta.type === 'book' ? (
              <BookCard metadata={meta} isOwn={isOwn} />
            ) : meta.type === 'audio' ? (
              <AudioMessage
                dataUrl={meta.dataUrl}
                duration={meta.duration}
                peaks={meta.peaks}
                isOwn={isOwn}
              />
            ) : (
              <FileChip metadata={meta} isOwn={isOwn} />
            )
          ) : (
            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
              {message.content}
            </p>
          )}
        </div>
        {/* Timestamp below the bubble — subtle, mono, right-aligned */}
        <span className="px-1 font-mono text-[7px] text-muted-foreground/60 leading-none text-right">
          {formatMessageTime(message.createdAt, t)}
        </span>
      </div>
    </motion.div>
  );
}

// ----- Profile Modal (AAA redesign) -----

function getProfessionalTypeLabel(
  t: ReturnType<typeof useTranslation>['t'],
  type: string
): string {
  const map: Record<string, string> = {
    editor: t('messagesView.typeEditor', 'Editor'),
    designer: t('messagesView.typeDesigner', 'Designer'),
    proofreader: t('messagesView.typeProofreader', 'Revisor'),
    ghostwriter: t('messagesView.typeGhostwriter', 'Ghostwriter'),
    translator: t('messagesView.typeTranslator', 'Tradutor'),
    beta_reader: t('messagesView.typeBetaReader', 'Beta Reader'),
  };
  return map[type] ?? type;
}

function StarRating({ rating, max = 5 }: { rating: number; max?: number }) {
  // rating may be 0-100 scale (professionals-view uses rating/100)
  // Normalize: if rating > 5, assume it's on a 100-point scale
  const normalized = rating > 5 ? rating / 100 : rating;
  const full = Math.floor(normalized);
  const partial = normalized - full;

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => {
        const filled = i < full;
        const halfFilled = !filled && i === full && partial >= 0.5;
        return (
          <Star
            key={i}
            size={13}
            className={cn(
              filled || halfFilled
                ? 'fill-accent text-accent'
                : 'text-muted-foreground/30'
            )}
          />
        );
      })}
    </div>
  );
}

function ProfileModal({
  open,
  onClose,
  user,
  professional,
}: {
  open: boolean;
  onClose: () => void;
  user: ParticipantUser;
  professional: ProfessionalInfo | null | undefined;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rating = professional?.rating
    ? professional.rating > 5
      ? professional.rating / 100
      : professional.rating
    : null;

  return (
    <ResponsiveModal open={open} onOpenChange={(o) => !o && onClose()}>
      <ResponsiveModalContent className="sm:max-w-md p-0 overflow-hidden">
        <ResponsiveModalTitle className="sr-only">
          {t('messagesView.profileOf', 'Perfil de {{name}}', { name: user.name })}
        </ResponsiveModalTitle>

        {/* Gradient header strip */}
        <div className="relative h-24 bg-gradient-to-br from-primary/70 via-accent/50 to-primary/30 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-accent/30 via-transparent to-transparent" />
        </div>

        {/* Avatar overlapping the strip */}
        <div className="relative px-6 -mt-10 pb-0">
          <div className="flex items-end gap-4">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="w-20 h-20 rounded-xl object-cover border-4 border-card shadow-xl flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground text-2xl font-bold flex-shrink-0 border-4 border-card shadow-xl select-none">
                {getInitials(user.name)}
              </div>
            )}
            {professional?.isAvailable && (
              <span className="mb-3 text-[10px] font-mono font-semibold px-2.5 py-1 rounded-full border bg-[rgba(94,234,212,0.1)] border-[rgba(94,234,212,0.3)] text-[#1F8A76] dark:text-[color:var(--aurora)] uppercase tracking-wide">
                {t('messagesView.available', 'Disponível')}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-4 space-y-4">
          {/* Name + type */}
          <div>
            <h2 className="text-xl font-serif font-semibold text-foreground leading-tight">
              {user.name}
            </h2>
            {professional && (
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full border bg-secondary text-secondary-foreground border-border uppercase tracking-wider">
                  {getProfessionalTypeLabel(t, professional.type)}
                </span>
                {professional.title && (
                  <span className="text-sm text-primary font-medium">
                    {professional.title}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Rating + reviews + rate */}
          {professional && rating != null && (
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <StarRating rating={professional.rating ?? 0} />
                <span className="text-sm font-semibold text-foreground">
                  {rating.toFixed(1)}
                </span>
                {professional.reviewCount != null && (
                  <span className="text-xs text-muted-foreground font-mono">
                    {t('messagesView.reviewCount', '({{count}} avaliações)', { count: professional.reviewCount })}
                  </span>
                )}
              </div>
              {professional.hourlyRate != null && (
                <span className="text-sm font-mono font-bold text-accent">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                  }).format(professional.hourlyRate / 100)}
                  /hr
                </span>
              )}
            </div>
          )}

          {/* Bio */}
          {professional?.bio && (
            <p className="text-sm text-foreground/80 leading-relaxed bg-muted/40 rounded-lg p-4 border border-border">
              {professional.bio}
            </p>
          )}

          {/* Specialties */}
          {professional?.specialties && professional.specialties.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                {t('messagesView.specialties', 'Especialidades')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {professional.specialties.map((s) => (
                  <span
                    key={s}
                    className="text-xs font-mono px-2.5 py-1 rounded-full border bg-secondary text-secondary-foreground border-border"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Languages with flags */}
          {professional?.languages && professional.languages.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                {t('messagesView.languages', 'Idiomas')}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                {professional.languages.map((lang) => {
                  const code = getLanguageFlag(lang);
                  return (
                    <div key={lang} className="flex items-center gap-1.5">
                      {code ? (
                        <span
                          className={`fi fi-${code}`}
                          style={{ width: '20px', height: '14px', borderRadius: '3px' }}
                        />
                      ) : null}
                      <span className="text-sm text-foreground/80">{lang}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA */}
          {professional && (
            <button
              onClick={() => {
                onClose();
                navigate('/app/professionals');
              }}
              className={cn(
                'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg',
                'bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors mt-2',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <ExternalLink size={14} />
              {t('messagesView.viewInMarketplace', 'Ver no marketplace')}
            </button>
          )}
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

// Book selector modal
function BookSelectorModal({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (project: { id: string; title: string; coverImage?: string }) => void;
}) {
  const { t } = useTranslation();
  const projects = useAppStore((s) => s.projects);

  return (
    <ResponsiveModal open={open} onOpenChange={(o) => !o && onClose()}>
      <ResponsiveModalContent className="sm:max-w-sm">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{t('messagesView.linkBook', 'Vincular livro')}</ResponsiveModalTitle>
        </ResponsiveModalHeader>
        <div className="py-2 space-y-1 max-h-[60vh] overflow-y-auto">
          {projects.length === 0 ? (
            <EmptyState
              icon={<BookOpen size={28} />}
              title={t('messagesView.noBooksYetTitle', 'Nenhum livro ainda.')}
              description={t('messagesView.noBooksYetDesc', 'Crie um livro para poder compartilhá-lo aqui.')}
              className="py-8"
            />
          ) : (
            projects.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  onSelect({ id: p.id, title: p.metadata.title, coverImage: p.metadata.coverImage });
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {p.metadata.coverImage ? (
                  <img
                    src={p.metadata.coverImage}
                    alt={p.metadata.title}
                    className="w-8 h-11 object-cover rounded-md flex-shrink-0 shadow-sm"
                  />
                ) : (
                  <div className="w-8 h-11 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                    <BookOpen size={14} className="text-primary" />
                  </div>
                )}
                <span className="text-sm font-medium truncate">{p.metadata.title || t('messagesView.untitled', 'Sem título')}</span>
              </button>
            ))
          )}
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

// ----- Main component -----

export function MessagesView() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const fetchProjects = useAppStore((s) => s.fetchProjects);
  const { resolvedTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<
    (Conversation & { messages?: Message[] }) | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Tracks the conversation id whose messages have already had their initial
  // (instant) scroll-to-bottom performed — anything after that scrolls smooth.
  const lastScrolledConvIdRef = useRef<string | null>(null);

  // ── Voice recording (WhatsApp-style) ──────────────────────────────────────
  const voice = useVoiceRecorder();
  const [recLocked, setRecLocked] = useState(false);
  const [recCancelArmed, setRecCancelArmed] = useState(false);
  // Pointer/gesture bookkeeping for press-hold + slide-to-cancel + slide-to-lock.
  const pressStartRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const cancelArmedRef = useRef(false);

  // Modal/panel states
  const [profileOpen, setProfileOpen] = useState(false);
  const [bookSelectorOpen, setBookSelectorOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [otherUserProfessional, setOtherUserProfessional] = useState<
    ProfessionalInfo | null | undefined
  >(undefined); // undefined = not yet loaded, null = not a professional

  // Derive the "other" participant for the active conversation
  const activeOtherUser = activeConversation
    ? resolveOtherUser(activeConversation, user?.id)
    : null;

  // Fetch conversation messages
  const fetchConversationMessages = useCallback(
    async (conversationId: string, baseConversation: Conversation) => {
      try {
        const response = await fetch(
          `${API_BASE}/messages/conversations/${conversationId}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          const merged: Conversation & { messages?: Message[] } = {
            ...data.conversation,
            otherParticipants:
              data.conversation.otherParticipants ??
              baseConversation.otherParticipants ??
              data.conversation.participants?.filter(
                (p: Participant) => p.userId !== user?.id
              ) ??
              [],
          };
          setActiveConversation(merged);
        }
      } catch (error) {
        console.error('Failed to fetch messages:', error);
      }
    },
    [user?.id]
  );

  const handleSelectConversation = useCallback(
    (conversation: Conversation) => {
      // Reset so the next populated render scrolls instantly (no smooth
      // top-to-bottom animation) — see the scroll effect below.
      lastScrolledConvIdRef.current = null;
      setActiveConversation({ ...conversation, messages: [] });
      fetchConversationMessages(conversation.id, conversation);
      // Reflect the open conversation in the URL so the app shell can hide
      // the mobile bottom tab bar only while a thread is actually open —
      // the conversation list itself keeps it (consistent with Professionals).
      setSearchParams({ c: conversation.id }, { replace: true });
    },
    [fetchConversationMessages, setSearchParams]
  );

  // Close the open conversation and return to the list — the single source
  // of truth for "back" navigation inside Messages (used by the icon button
  // and the Esc shortcut).
  const handleBackToList = useCallback(() => {
    setActiveConversation(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Fetch conversations on mount
  useEffect(() => {
    const fetchConversations = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE}/messages/conversations`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          const convList: Conversation[] = data.conversations;
          setConversations(convList);

          // Auto-select conversation from URL param ?c=<id>
          const targetId = searchParams.get('c');
          if (targetId) {
            const target = convList.find((c) => c.id === targetId);
            if (target) {
              // handleSelectConversation re-syncs the `c` param itself (replace),
              // so no separate URL cleanup is needed here.
              handleSelectConversation(target);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch conversations:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConversations();
    // Also load projects for book selector
    fetchProjects().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount; searchParams captured in closure

  // Fetch professional info when the other user changes
  useEffect(() => {
    if (!activeOtherUser) {
      setOtherUserProfessional(undefined);
      return;
    }
    setOtherUserProfessional(undefined);
    api
      .getProfessionalByUserId(activeOtherUser.id)
      .then((prof) => setOtherUserProfessional(prof ?? null))
      .catch(() => setOtherUserProfessional(null));
  }, [activeOtherUser?.id]);

  // Core send function — accepts optional metadata
  const doSendMessage = useCallback(
    async (content: string, metadata?: MessageMetadata) => {
      if (!activeConversation || isSending) return;
      setIsSending(true);

      const optimisticMsg: Message = {
        id: `optimistic-${Date.now()}`,
        conversationId: activeConversation.id,
        senderId: user?.id ?? '',
        content,
        metadata: metadata ?? null,
        createdAt: new Date().toISOString(),
        sender: {
          id: user?.id ?? '',
          name: user?.name ?? '',
          avatarUrl: undefined,
        },
      };

      setActiveConversation((prev) =>
        prev ? { ...prev, messages: [...(prev.messages ?? []), optimisticMsg] } : null
      );

      try {
        const response = await fetch(
          `${API_BASE}/messages/conversations/${activeConversation.id}/messages`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content, ...(metadata ? { metadata } : {}) }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          setActiveConversation((prev) =>
            prev
              ? {
                  ...prev,
                  messages: (prev.messages ?? []).map((m) =>
                    m.id === optimisticMsg.id ? data.message : m
                  ),
                }
              : null
          );
          setConversations((prev) =>
            prev.map((c) =>
              c.id === activeConversation.id
                ? { ...c, lastMessage: data.message, updatedAt: new Date().toISOString() }
                : c
            )
          );
        } else {
          setActiveConversation((prev) =>
            prev
              ? { ...prev, messages: (prev.messages ?? []).filter((m) => m.id !== optimisticMsg.id) }
              : null
          );
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        setActiveConversation((prev) =>
          prev
            ? { ...prev, messages: (prev.messages ?? []).filter((m) => m.id !== optimisticMsg.id) }
            : null
        );
      } finally {
        setIsSending(false);
      }
    },
    [activeConversation, isSending, user]
  );

  // Send plain text message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;
    const content = newMessage.trim();
    setNewMessage('');
    await doSendMessage(content);
    // a pena leva a mensagem
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'feather' } }));
  };

  // Emoji picker
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = newMessage.slice(0, start);
      const after = newMessage.slice(end);
      const updated = before + emoji + after;
      setNewMessage(updated);
      // Restore caret position after state update
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setNewMessage((prev) => prev + emoji);
    }
    setEmojiPickerOpen(false);
  };

  // Ver Perfil
  const handleViewProfile = () => {
    if (!activeOtherUser) return;
    setProfileOpen(true);
  };

  // Vincular Livro
  const handleBookSelected = async (project: {
    id: string;
    title: string;
    coverImage?: string;
  }) => {
    await doSendMessage(
      t('messagesView.sharedBookMessage', 'Compartilhei o livro "{{title}}"', { title: project.title }),
      {
        type: 'book',
        projectId: project.id,
        title: project.title,
        coverImage: project.coverImage,
      }
    );
  };

  // Anexar Arquivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!e.target.files) return;
    e.target.value = '';
    if (!file) return;

    const MAX_SIZE = 2 * 1024 * 1024; // 2 MB
    if (file.size > MAX_SIZE) {
      showToast(t('messagesView.fileTooLarge', 'Arquivo muito grande. Limite: 2 MB.'), true);
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      await doSendMessage(
        t('messagesView.fileMessage', 'Arquivo: {{name}}', { name: file.name }),
        {
          type: 'file',
          name: file.name,
          mime: file.type,
          dataUrl,
          size: file.size,
        }
      );
    };
    reader.readAsDataURL(file);
  };

  // ── Voice message handlers ────────────────────────────────────────────────
  const sendRecording = useCallback(async () => {
    setRecLocked(false);
    setRecCancelArmed(false);
    cancelArmedRef.current = false;
    const rec = await voice.stop();
    if (!rec) return;
    if (rec.duration < 0.6) {
      showToast(t('messagesView.audioTooShort', 'Áudio muito curto.'), true);
      URL.revokeObjectURL(rec.url);
      return;
    }
    const MAX_SIZE = 5 * 1024 * 1024; // ~5 MB
    if (rec.blob.size > MAX_SIZE) {
      showToast(t('messagesView.audioTooLong', 'Áudio muito longo. Grave um trecho menor.'), true);
      URL.revokeObjectURL(rec.url);
      return;
    }
    try {
      const dataUrl = await blobToDataUrl(rec.blob);
      await doSendMessage(t('messagesView.voiceMessage', '🎤 Mensagem de voz'), {
        type: 'audio',
        dataUrl,
        mime: rec.mime,
        size: rec.blob.size,
        duration: Math.round(rec.duration),
        peaks: rec.peaks.map((v) => Math.round(v * 100) / 100),
      });
    } finally {
      URL.revokeObjectURL(rec.url);
    }
  }, [voice, doSendMessage, t]);

  const cancelRecording = useCallback(async () => {
    setRecLocked(false);
    setRecCancelArmed(false);
    cancelArmedRef.current = false;
    await voice.cancel();
  }, [voice]);

  // Press-hold the mic to record. Slide left → cancel, slide up → lock (hands-free),
  // quick tap → lock. Release → send. Works with mouse and touch (pointer events).
  const handleMicPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (voice.recording) return;
      e.preventDefault();
      const start = { x: e.clientX, y: e.clientY, t: Date.now() };
      pressStartRef.current = start;
      cancelArmedRef.current = false;
      setRecLocked(false);
      setRecCancelArmed(false);

      const detach = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
      };
      const onMove = (ev: PointerEvent) => {
        if (!pressStartRef.current) return;
        const dx = ev.clientX - start.x;
        const dy = ev.clientY - start.y;
        if (dy < -60) {
          // Slide up → lock, recording continues hands-free.
          detach();
          pressStartRef.current = null;
          cancelArmedRef.current = false;
          setRecCancelArmed(false);
          setRecLocked(true);
          return;
        }
        const armed = dx < -70;
        if (armed !== cancelArmedRef.current) {
          cancelArmedRef.current = armed;
          setRecCancelArmed(armed);
        }
      };
      const onEnd = (ev: PointerEvent) => {
        detach();
        pressStartRef.current = null;
        // A pointercancel (e.g. the mic button unmounting into the recording bar)
        // is neutral — keep recording locked so the bar's buttons take over.
        if (ev.type === 'pointercancel') {
          setRecLocked(true);
          return;
        }
        const held = Date.now() - start.t;
        if (cancelArmedRef.current) {
          void cancelRecording();
        } else if (held < 350) {
          // Quick tap → keep recording locked (hands-free).
          setRecLocked(true);
        } else {
          void sendRecording();
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onEnd);
      window.addEventListener('pointercancel', onEnd);

      void voice.start().then((ok) => {
        if (!ok) {
          detach();
          pressStartRef.current = null;
          showToast(t('messagesView.micPermissionDenied', 'Não foi possível acessar o microfone. Verifique as permissões.'), true);
        }
      });
    },
    [voice, cancelRecording, sendRecording, t]
  );

  // Scroll to bottom when messages change. The FIRST time a conversation's
  // messages populate, jump instantly (no visible top-to-bottom animation);
  // subsequent arrivals (new messages while already open) scroll smoothly.
  useEffect(() => {
    if (!activeConversation) return;
    const hasMessages = (activeConversation.messages?.length ?? 0) > 0;
    const isInitialLoad = lastScrolledConvIdRef.current !== activeConversation.id;
    if (isInitialLoad && !hasMessages) return; // still loading placeholder — wait for real content
    messagesEndRef.current?.scrollIntoView({ behavior: isInitialLoad ? 'auto' : 'smooth' });
    lastScrolledConvIdRef.current = activeConversation.id;
  }, [activeConversation?.id, activeConversation?.messages]);

  // Close emoji picker on outside click
  useEffect(() => {
    if (!emojiPickerOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-emoji-picker]') && !target.closest('[data-emoji-btn]')) {
        setEmojiPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [emojiPickerOpen]);

  const filteredConversations = conversations.filter((c) => {
    if (!searchQuery) return true;
    const otherUser = resolveOtherUser(c, user?.id);
    return otherUser?.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const emojiTheme = resolvedTheme === 'dark' ? EmojiTheme.DARK : EmojiTheme.LIGHT;

  // Chat keyboard shortcuts
  useKeyboardShortcuts([
    {
      // mod+/ → toggle emoji picker
      combo: 'mod+/',
      handler: () => {
        if (activeConversation) {
          setEmojiPickerOpen((o) => !o);
        }
      },
      allowInInputs: true,
    },
    {
      // Esc → close profile modal, emoji picker, or go back to conversation list
      combo: 'esc',
      handler: () => {
        if (profileOpen) {
          setProfileOpen(false);
        } else if (emojiPickerOpen) {
          setEmojiPickerOpen(false);
        } else if (bookSelectorOpen) {
          setBookSelectorOpen(false);
        } else if (activeConversation) {
          handleBackToList();
        }
      },
      allowInInputs: true,
    },
  ]);

  return (
    <div className="flex-1 flex h-full overflow-hidden bg-background">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.txt,.docx"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Profile modal */}
      {activeOtherUser && (
        <ProfileModal
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          user={activeOtherUser}
          professional={otherUserProfessional}
        />
      )}

      {/* Book selector modal */}
      <BookSelectorModal
        open={bookSelectorOpen}
        onClose={() => setBookSelectorOpen(false)}
        onSelect={handleBookSelected}
      />

      {/* ===== Conversation List ===== */}
      <div
        className={cn(
          'w-full md:w-72 lg:w-80 border-r border-border flex flex-col bg-transparent shrink-0',
          activeConversation && 'hidden md:flex'
        )}
      >
        {/* List header — eyebrow + serif title, matching the Notifications
            header pattern (no icon, no top-level back button). The
            conversation list is a top-level destination reached via the
            bottom tab bar, same as Professionals and Notifications —
            "back" only applies once a thread is open (see ArrowLeft below). */}
        <div className="shrink-0 border-b border-border">
          <div className="px-4 pt-4 pb-3">
            <p className="m-eyebrow">{t('messages.title', 'Mensagens')}</p>
            <h1 className="font-serif text-[22px] font-medium leading-snug text-foreground mt-1.5 truncate">
              {t('messages.subtitle', 'Suas conversas')}
            </h1>
          </div>
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder={t('messages.search', 'Buscar conversas...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  'w-full pl-9 pr-4 py-2 rounded-lg bg-muted border-0 text-sm',
                  'text-foreground placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/40'
                )}
              />
            </div>
          </div>
        </div>

        {/* Conversations list */}
        <div className="flex-1 min-h-0 overflow-y-auto [-webkit-overflow-scrolling:touch]">
          {isLoading ? (
            <div className="py-2">
              {[1, 2, 3, 4].map((i) => (
                <ConversationSkeleton key={i} />
              ))}
            </div>
          ) : filteredConversations.length === 0 ? (
            conversations.length === 0 ? (
              <EmptyState
                icon={<MessageSquare size={32} />}
                title={t('messagesView.noConversationsTitle', 'Ainda quieto por aqui.')}
                description={t('messagesView.noConversationsDesc', 'Encontre um profissional no marketplace e inicie uma conversa.')}
                className="py-12"
              />
            ) : (
              <EmptyState
                icon={<MessageSquare size={28} />}
                title={t('messagesView.noSearchResultsTitle', 'Nenhuma conversa com esse nome.')}
                description={t('messagesView.noSearchResultsDesc', 'Tente outro termo de busca.')}
                className="py-10"
              />
            )
          ) : (
            <div className="py-2">
              {filteredConversations.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isActive={activeConversation?.id === conversation.id}
                  currentUserId={user?.id}
                  onClick={() => handleSelectConversation(conversation)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ===== Message Thread ===== */}
      {activeConversation ? (
        <div className="flex-1 flex flex-col min-w-0">
          {/* Thread header */}
          <div className="h-14 border-b border-border flex items-center justify-between px-4 bg-transparent flex-shrink-0 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* Back button — mobile only */}
              <button
                className={cn(
                  'md:hidden p-1.5 -ml-1 rounded-lg text-muted-foreground',
                  'hover:text-foreground hover:bg-muted transition-colors flex-shrink-0'
                )}
                onClick={handleBackToList}
                aria-label={t('common.back', 'Voltar')}
              >
                <ArrowLeft size={18} />
              </button>

              {activeOtherUser ? (
                activeOtherUser.avatarUrl ? (
                  <img
                    src={activeOtherUser.avatarUrl}
                    alt={activeOtherUser.name}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-serif font-semibold text-sm flex-shrink-0 select-none"
                    style={{
                      background: 'linear-gradient(135deg,var(--aurora),#2E9E8A)',
                      color: '#04211C',
                    }}
                  >
                    {getInitials(activeOtherUser.name)}
                  </div>
                )
              ) : (
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <UserCircle size={18} className="text-muted-foreground" />
                </div>
              )}

              <div className="min-w-0">
                <h3 className="font-serif font-medium text-foreground leading-tight text-[12.5px] truncate">
                  {activeOtherUser?.name ?? t('messagesView.conversationFallback', 'Conversa')}
                </h3>
                <p
                  className="font-mono text-[7px] uppercase tracking-widest leading-none mt-0.5 truncate"
                  style={{ color: 'var(--aurora)' }}
                >
                  {otherUserProfessional
                    ? getProfessionalTypeLabel(t, otherUserProfessional.type)
                    : t('messages.professional', 'Profissional')}
                </p>
              </div>
            </div>

            {/* Header actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={handleViewProfile}
                title={t('messagesView.viewProfile', 'Ver perfil')}
                className={cn(
                  'p-2 rounded-lg text-muted-foreground',
                  'hover:text-foreground hover:bg-muted transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
              >
                <UserCircle size={17} />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger
                  title={t('messagesView.moreOptions', 'Mais opções')}
                  className={cn(
                    'p-2 rounded-lg text-muted-foreground',
                    'hover:text-foreground hover:bg-muted transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                >
                  <MoreVertical size={17} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[180px]">
                  <DropdownMenuItem onClick={handleViewProfile}>
                    <UserCircle size={14} className="text-muted-foreground" />
                    {t('messagesView.viewProfile', 'Ver perfil')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBookSelectorOpen(true)}>
                    <BookOpen size={14} className="text-muted-foreground" />
                    {t('messagesView.linkBook', 'Vincular livro')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
            {!activeConversation.messages ||
            activeConversation.messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <EmptyState
                  icon={<MessageSquare size={32} />}
                  title={t('messagesView.conversationNotStartedTitle', 'A conversa ainda não começou.')}
                  description={t('messagesView.conversationNotStartedDesc', 'Diga a primeira palavra — uma pergunta, uma ideia, um trecho do livro.')}
                  className="py-0"
                />
              </div>
            ) : (
              activeConversation.messages.map((message, index) => {
                const isOwn = message.senderId === user?.id;
                const prevMessage = activeConversation.messages?.[index - 1];
                const showAvatar =
                  !isOwn &&
                  (!prevMessage || prevMessage.senderId !== message.senderId);

                const currDate = new Date(message.createdAt).toDateString();
                const prevDate = prevMessage
                  ? new Date(prevMessage.createdAt).toDateString()
                  : null;
                const showDateSep = !prevDate || currDate !== prevDate;

                return (
                  <div key={message.id}>
                    {showDateSep && (
                      <div className="flex justify-center py-2 my-1">
                        <span className="font-mono text-[7px] uppercase tracking-widest text-muted-foreground px-2.5 py-1 rounded-full bg-foreground/[0.07]">
                          {new Date(message.createdAt).toLocaleDateString([], {
                            day: 'numeric',
                            month: 'short',
                            year:
                              new Date(message.createdAt).getFullYear() !==
                              new Date().getFullYear()
                                ? 'numeric'
                                : undefined,
                          })}
                        </span>
                      </div>
                    )}
                    <MessageBubble
                      message={message}
                      isOwn={isOwn}
                      showAvatar={showAvatar}
                    />
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Composer */}
          <div className="px-4 py-3 border-t border-border bg-transparent flex-shrink-0">
            {voice.recording ? (
              <RecordingBar
                elapsed={voice.elapsed}
                levels={voice.levels}
                locked={recLocked}
                cancelArmed={recCancelArmed}
                onCancel={cancelRecording}
                onSend={sendRecording}
              />
            ) : (
            <>
            {/* Composer — icons + textarea + send/mic ALL on one row, never wraps */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex items-center gap-0.5"
            >
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title={t('messagesView.attachFile', 'Anexar arquivo (máx 2 MB) — imagens, PDF, DOCX')}
                className={cn(
                  'p-2 rounded-lg text-muted-foreground transition-colors shrink-0',
                  'hover:text-foreground hover:bg-muted',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
              >
                <Paperclip size={16} />
              </button>

              <button
                type="button"
                onClick={() => setBookSelectorOpen(true)}
                title={t('messagesView.linkBookProject', 'Vincular livro/projeto')}
                className={cn(
                  'p-2 rounded-lg text-muted-foreground transition-colors shrink-0',
                  'hover:text-foreground hover:bg-muted',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
              >
                <BookOpen size={16} />
              </button>

              {/* Emoji button + picker */}
              <div className="relative shrink-0">
                <button
                  data-emoji-btn
                  type="button"
                  onClick={() => setEmojiPickerOpen((o) => !o)}
                  title={t('messagesView.insertEmoji', 'Inserir emoji')}
                  className={cn(
                    'p-2 rounded-lg text-muted-foreground transition-colors',
                    'hover:text-foreground hover:bg-muted',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    emojiPickerOpen && 'text-foreground bg-muted'
                  )}
                >
                  <Smile size={16} />
                </button>
                {emojiPickerOpen && (
                  <div
                    data-emoji-picker
                    className="absolute bottom-11 left-0 z-50 shadow-2xl rounded-xl overflow-hidden"
                  >
                    <EmojiPicker
                      theme={emojiTheme}
                      onEmojiClick={handleEmojiClick}
                      lazyLoadEmojis
                      searchPlaceHolder={t('messagesView.searchEmojiPlaceholder', 'Buscar emoji...')}
                      height={380}
                      width={320}
                    />
                  </div>
                )}
              </div>

              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={t('messages.typePlaceholder', 'Escreva uma mensagem... (Enter para enviar, Shift+Enter para nova linha)')}
                rows={1}
                className={cn(
                  'flex-1 min-w-0 px-4 py-2.5 rounded-full bg-card border-0 shadow-[0_0_0_1px_var(--border)_inset]',
                  'text-foreground placeholder:text-muted-foreground text-[11px] sm:text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary/40',
                  'resize-none min-h-[40px] max-h-[120px] overflow-y-auto leading-relaxed'
                )}
                style={{ height: 'auto' }}
                disabled={isSending}
                onInput={(e) => {
                  const el = e.currentTarget;
                  el.style.height = 'auto';
                  el.style.height = `${el.scrollHeight}px`;
                }}
              />
              {newMessage.trim() ? (
                <button
                  type="submit"
                  disabled={isSending}
                  className={cn(
                    'p-2.5 rounded-full shrink-0',
                    'disabled:opacity-40 disabled:cursor-not-allowed',
                    'transition-transform active:scale-95',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                  style={{
                    background: 'linear-gradient(135deg,var(--ember-hot),var(--ember))',
                    color: '#141005',
                    boxShadow: '0 0 18px rgba(223,201,147,.5)',
                  }}
                  aria-label={t('messagesView.sendMessage', 'Enviar mensagem')}
                >
                  {isSending ? (
                    <Loader2 size={17} className="animate-spin" />
                  ) : (
                    <Send size={17} />
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onPointerDown={handleMicPointerDown}
                  disabled={isSending}
                  title={t('messagesView.micInstructions', 'Segure para gravar · toque para travar · arraste ← para cancelar')}
                  aria-label={t('messagesView.recordAudio', 'Gravar áudio')}
                  className={cn(
                    'p-2.5 rounded-full shrink-0',
                    'disabled:opacity-40 transition-transform active:scale-95',
                    'touch-none select-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                  style={{
                    background: 'linear-gradient(135deg,var(--ember-hot),var(--ember))',
                    color: '#141005',
                    boxShadow: '0 0 18px rgba(223,201,147,.5)',
                  }}
                >
                  <Mic size={17} />
                </button>
              )}
            </form>
            </>
            )}
          </div>
        </div>
      ) : (
        /* Empty / no-conversation-selected state */
        <div className="hidden md:flex flex-1 items-center justify-center bg-transparent">
          <EmptyState
            icon={<MessageSquare size={36} />}
            title={t('messagesView.chooseConversationTitle', 'Escolha uma conversa.')}
            description={t('messagesView.chooseConversationDesc', 'Selecione uma conversa à esquerda para ler e responder mensagens.')}
            className="py-0"
          />
        </div>
      )}
    </div>
  );
}
